import { useTranslation } from "react-i18next";
import JourneyDisplay from "@pages/booking/cart/journey/Journey";
import { Status, Travel } from "@gen/travel/v1/common_pb";
import i18n from "@libs/i18n";

import { Place } from "@gen/place/v1/place_pb";
import { Button, Icon } from "@thetreep/ui";
import { PaymentDetails } from "./PaymentDetails";
import { localDateTimeToDate } from "@utils/date";

type TravelDisplayProps = {
  travel: Travel;
  onCancel?: () => void;
  onExchange?: () => void;
};

export function UserTravel({
  travel,
  onCancel,
  onExchange,
}: TravelDisplayProps) {
  const { t } = useTranslation();

  if (
    !travel ||
    !travel.outward?.schedule?.departure ||
    !travel.inward?.schedule?.arrival
  ) {
    return null;
  }

  const startDate = localDateTimeToDate(travel.outward?.schedule?.departure);
  const endDate = localDateTimeToDate(travel.inward?.schedule?.arrival);

  function formatDate(date: Date) {
    return Intl.DateTimeFormat(i18n.language, {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(date);
  }

  function displayCity(place: Place | undefined) {
    if (!place) return "";
    return place.city || place.name;
  }

  function isActive() {
    return (
      travel.status == Status.UPCOMING || travel.status == Status.IN_PROGRESS
    );
  }

  return (
    <div className="bg-white rounded-lg p-4 gap-4 lg:p-8 lg:gap-8">
      <div className="flex flex-col gap-4">
        <div className="flex flex-row justify-between gap-2 flex-wrap">
          <h2 className="font-jakarta text-[14px] font-bold lg:text-[20px] text-text-strong">
            {`${t("cart.outward")} ${displayCity(
              travel.outward.route?.origin,
            )} → ${displayCity(
              travel.outward.route?.destination,
            )}, ${formatDate(startDate)}`}
            <br />
            {`${t("cart.return")} ${displayCity(
              travel.inward.route?.origin,
            )} → ${displayCity(travel.inward.route?.destination)}, ${formatDate(
              endDate,
            )}`}
          </h2>
          <div className="flex flex-row gap-2 max-h-10">
            {isActive() && (
              <Button
                type="button"
                kind="outline"
                size="mini"
                onClick={onExchange}
                startEnhancer={<Icon color="#0c419a" src="pencil_line" />}
              >
                {t("modify")}
              </Button>
            )}

            {isActive() && (
              <Button
                type="button"
                kind="outline"
                size="mini"
                onClick={onCancel}
                startEnhancer={<Icon src="cross" />}
              >
                {t("userBookings.cancel")}
              </Button>
            )}
          </div>
        </div>

        <JourneyDisplay
          key={travel?.id + "-1"}
          travel={travel}
          journey={travel.outward}
          showStatus
        />
        <JourneyDisplay
          key={travel?.id + "-2"}
          travel={travel}
          journey={travel.inward}
          showStatus
        />
        <PaymentDetails travel={travel} />
      </div>
    </div>
  );
}
